
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Tool, Mode, IsometricObject, Point3D, Orientation } from './types';
import { GRID_SIZE, COLORS, INITIAL_ROTATION } from './constants';
import { project, sortObjects } from './utils/isometricMath';
import { Icon } from './components/Icon';
import { CubeRenderer } from './components/CubeRenderer';
import { FaceRenderer } from './components/FaceRenderer';
import { SegmentRenderer } from './components/SegmentRenderer';
import { IsometricGrid } from './components/IsometricGrid';

const AXIS_COLORS = {
  X: '#EF4444',
  Y: '#10B981',
  Z: '#3B82F6'
};

const OrthographicView: React.FC<{ 
  objects: IsometricObject[], 
  title: string, 
  direction: 'front' | 'top' | 'right' | 'left',
  size?: number 
}> = ({ objects, title, direction, size = 140 }) => {
  const cubeMap = useMemo(() => {
    const map: Record<string, string> = {};
    objects.forEach(obj => {
      if (obj.type !== 'cube') return;
      
      let key = '';
      let depth = 0;
      
      if (direction === 'top') {
        key = `${obj.position.x},${obj.position.z}`;
        depth = obj.position.y;
      } else if (direction === 'front') {
        key = `${obj.position.x},${obj.position.y}`;
        depth = -obj.position.z;
      } else if (direction === 'right') {
        key = `${obj.position.z},${obj.position.y}`;
        depth = -obj.position.x;
      } else if (direction === 'left') {
        key = `${GRID_SIZE - 1 - obj.position.z},${obj.position.y}`;
        depth = obj.position.x;
      }
      
      if (!map[key] || depth > parseInt((map[key] as string).split('|')[1])) {
        map[key] = `${obj.color}|${depth}`;
      }
    });
    return map;
  }, [objects, direction]);

  const gridRes = GRID_SIZE * 10;
  
  const getLabels = () => {
    if (direction === 'top') return { h: 'X', v: 'Z', hCol: AXIS_COLORS.X, vCol: AXIS_COLORS.Z };
    if (direction === 'front') return { h: 'X', v: 'Y', hCol: AXIS_COLORS.X, vCol: AXIS_COLORS.Y };
    if (direction === 'right') return { h: 'Z', v: 'Y', hCol: AXIS_COLORS.Z, vCol: AXIS_COLORS.Y };
    return { h: 'Z', v: 'Y', hCol: AXIS_COLORS.Z, vCol: AXIS_COLORS.Y };
  };
  
  const labels = getLabels();

  return (
    <div className="flex flex-col items-center p-2 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl shadow-sm">
      <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</span>
      <div className="relative">
        <svg width={size} height={size} viewBox={`0 0 ${gridRes} ${gridRes}`}>
           <rect width="100%" height="100%" fill="#f8fafc" rx="4" />
           {Object.entries(cubeMap).map(([key, value]) => {
             const [x, y] = key.split(',').map(Number);
             const color = (value as string).split('|')[0];
             return <rect key={key} x={x * 10} y={gridRes - (y + 1) * 10} width="10" height="10" fill={color} stroke="white" strokeWidth="0.5" />;
           })}
        </svg>
        <div className="absolute bottom-1 left-1 flex flex-col text-[7px] font-bold opacity-60">
          <div className="flex items-center space-x-1"><span style={{ color: labels.vCol }}>↑ {labels.v}</span></div>
          <div className="flex items-center space-x-1"><span style={{ color: labels.hCol }}>→ {labels.h}</span></div>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [mode, setMode] = useState<Mode>(Mode.CREATION);
  const [activeTool, setActiveTool] = useState<Tool>(Tool.CUBE);
  const [objects, setObjects] = useState<IsometricObject[]>([]);
  const [activeColor, setActiveColor] = useState<string>(COLORS[0]);
  const [showAxes, setShowAxes] = useState<boolean>(true);
  const [transparent, setTransparent] = useState<boolean>(false);
  const [view2D, setView2D] = useState<boolean>(false);
  
  const [hoveredPoint, setHoveredPoint] = useState<Point3D | null>(null);
  const [hoveredFacePos, setHoveredFacePos] = useState<Point3D | null>(null);
  const [hoveredOrientation, setHoveredOrientation] = useState<Orientation>('top');
  
  const [drawingSegmentStart, setDrawingSegmentStart] = useState<Point3D | null>(null);
  
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rotation, setRotation] = useState(INITIAL_ROTATION);
  const [isRotating, setIsRotating] = useState(false);
  const [showHelp, setShowHelp] = useState(true);
  const [showMiniMap, setShowMiniMap] = useState(true);

  const svgRef = useRef<SVGSVGElement>(null);

  const cubesCount = useMemo(() => objects.filter(o => o.type === 'cube').length, [objects]);

  // Mapa de cubos para búsqueda rápida de vecinos
  const cubeLookup = useMemo(() => {
    const map = new Map<string, boolean>();
    objects.forEach(obj => {
      if (obj.type === 'cube') {
        map.set(`${obj.position.x},${obj.position.y},${obj.position.z}`, true);
      }
    });
    return map;
  }, [objects]);

  const gridPoints2D = useMemo(() => {
    const pts = [];
    for (let x = -GRID_SIZE/2; x <= GRID_SIZE/2; x++) {
      for (let z = -GRID_SIZE/2; z <= GRID_SIZE/2; z++) {
        pts.push({ coords3D: { x, y: 0, z }, coords2D: project({ x, y: 0, z }, rotation) });
      }
    }
    return pts;
  }, [rotation]);

  useEffect(() => {
    if (objects.length > 0) setShowHelp(false);
  }, [objects.length]);

  const addObject = useCallback((p: Point3D, orientation?: Orientation, endP?: Point3D) => {
    if (activeTool === Tool.ERASER) {
      const existing = objects.find(o => o.position.x === p.x && o.position.y === p.y && o.position.z === p.z);
      if (existing) removeObject(existing.id);
      return;
    }
    
    if (activeTool === Tool.CUBE) {
      const exists = objects.some(o => o.type === 'cube' && o.position.x === p.x && o.position.y === p.y && o.position.z === p.z);
      if (exists) return;
    }

    const finalOrientation = orientation || hoveredOrientation;

    const newObj: IsometricObject = {
      id: Math.random().toString(36).substr(2, 9),
      type: activeTool === Tool.CUBE ? 'cube' : activeTool === Tool.FACE ? 'face' : 'segment',
      position: { ...p },
      color: activeColor,
      orientation: finalOrientation,
      endPosition: endP ? { ...endP } : undefined,
    };

    setObjects(prev => [...prev, newObj]);
  }, [activeTool, activeColor, objects, hoveredOrientation]);

  const removeObject = useCallback((id: string) => {
    setObjects(prev => prev.filter(o => o.id !== id));
    setSelectedIds(prev => prev.filter(sid => sid !== id));
  }, []);

  const handleFaceClick = (pos: Point3D, face: Orientation) => {
    if (mode === Mode.INSPECTION) return;
    
    const nextPos = { ...pos };
    if (face === 'top') nextPos.y += 1;
    if (face === 'left') nextPos.z += 1;
    if (face === 'right') nextPos.x += 1;

    if (activeTool === Tool.CUBE) {
      addObject(nextPos);
    } else if (activeTool === Tool.FACE) {
      addObject(pos, face);
    } else if (activeTool === Tool.SEGMENT) {
      if (!drawingSegmentStart) {
        setDrawingSegmentStart(nextPos);
      } else {
        addObject(drawingSegmentStart, undefined, nextPos);
        setDrawingSegmentStart(null);
      }
    } else if (activeTool === Tool.ERASER) {
      const obj = objects.find(o => o.position.x === pos.x && o.position.y === pos.y && o.position.z === pos.z);
      if (obj) removeObject(obj.id);
    } else if (activeTool === Tool.BRUSH) {
      const obj = objects.find(o => o.position.x === pos.x && o.position.y === pos.y && o.position.z === pos.z);
      if (obj) handleObjectClick(obj);
    }
  };

  const handleFaceHover = (pos: Point3D | null, face?: Orientation) => {
    if (!pos || !face || mode === Mode.INSPECTION) {
      setHoveredFacePos(null);
      return;
    }
    
    setHoveredOrientation(face);

    if (activeTool === Tool.CUBE || activeTool === Tool.SEGMENT) {
      const nextPos = { ...pos };
      if (face === 'top') nextPos.y += 1;
      if (face === 'left') nextPos.z += 1;
      if (face === 'right') nextPos.x += 1;
      setHoveredFacePos(nextPos);
    } else if (activeTool === Tool.FACE) {
      setHoveredFacePos(pos);
    }
  };

  const handleObjectClick = (obj: IsometricObject) => {
    if (activeTool === Tool.ERASER) {
      removeObject(obj.id);
    } else if (activeTool === Tool.POINTER) {
      setSelectedIds(prev => prev.includes(obj.id) ? prev.filter(id => id !== obj.id) : [...prev, obj.id]);
    } else if (activeTool === Tool.BRUSH) {
      setObjects(prev => prev.map(o => o.id === obj.id ? { ...o, color: activeColor } : o));
    }
  };

  const moveSelected = (dx: number, dy: number, dz: number) => {
    if (selectedIds.length === 0) return;
    setObjects(prev => prev.map(o => selectedIds.includes(o.id) ? {
      ...o, 
      position: { x: o.position.x + dx, y: o.position.y + dy, z: o.position.z + dz },
      endPosition: o.endPosition ? { x: o.endPosition.x + dx, y: o.endPosition.y + dy, z: o.endPosition.z + dz } : undefined
    } : o));
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isRotating) {
      setRotation(prev => ({ 
        x: Math.max(5, Math.min(85, prev.x + e.movementY * 0.5)), 
        y: prev.y + e.movementX * 0.5 
      }));
      return;
    }

    if (!svgRef.current || view2D) return;

    const CTM = svgRef.current.getScreenCTM();
    if (!CTM) return;
    
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(CTM.inverse());

    let closest = null;
    let minDist = 25; 

    for (const gridPt of gridPoints2D) {
      const dist = Math.sqrt(Math.pow(svgPt.x - gridPt.coords2D.x, 2) + Math.pow(svgPt.y - gridPt.coords2D.y, 2));
      if (dist < minDist) {
        minDist = dist;
        closest = gridPt.coords3D;
      }
    }
    
    if (closest?.x !== hoveredPoint?.x || closest?.y !== hoveredPoint?.y || closest?.z !== hoveredPoint?.z) {
      setHoveredPoint(closest);
      if (activeTool === Tool.FACE) {
         setHoveredOrientation('top');
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (activeTool === Tool.ROTATOR || mode === Mode.INSPECTION) {
      setIsRotating(true);
      return;
    } 
    
    if (mode === Mode.CREATION) {
      const targetPoint = hoveredFacePos || hoveredPoint;
      if (!targetPoint) return;

      if (activeTool === Tool.SEGMENT) {
        if (!drawingSegmentStart) {
          setDrawingSegmentStart(targetPoint);
        } else {
          addObject(drawingSegmentStart, undefined, targetPoint);
          setDrawingSegmentStart(null);
        }
      } else {
        addObject(targetPoint);
      }
    }
  };

  const handleMouseUp = () => setIsRotating(false);

  const sortedObjects = useMemo(() => sortObjects(objects, rotation), [objects, rotation]);

  const axisGuides = useMemo(() => {
    const ext = GRID_SIZE;
    const o = project({ x: 0, y: 0, z: 0 }, rotation);
    const xp = project({ x: ext, y: 0, z: 0 }, rotation);
    const xn = project({ x: -ext, y: 0, z: 0 }, rotation);
    const yp = project({ x: 0, y: ext, z: 0 }, rotation);
    const yn = project({ x: 0, y: -ext, z: 0 }, rotation);
    const zp = project({ x: 0, y: 0, z: ext }, rotation);
    const zn = project({ x: 0, y: 0, z: -ext }, rotation);
    return { o, xp, xn, yp, yn, zp, zn };
  }, [rotation]);

  const ghostPos = hoveredFacePos || hoveredPoint;

  return (
    <div className="flex flex-col h-screen overflow-hidden text-gray-800 bg-slate-50 font-sans selection:bg-blue-100" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      
      {showHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm no-print">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-black text-blue-600 uppercase tracking-tight">IsoDraw Pro</h2>
              <button onClick={() => setShowHelp(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><Icon name="reset" className="w-5 h-5 rotate-45" /></button>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-3 rounded-2xl text-blue-600"><Icon name="eye" /></div>
                <div>
                  <p className="font-bold text-slate-700">Ejes Educativos</p>
                  <p className="text-sm text-slate-500">
                    <span className="text-red-500 font-black">X+</span> Derecha, 
                    <span className="text-emerald-500 font-black"> Y+</span> Arriba, 
                    <span className="text-blue-500 font-black"> Z+</span> Fondo.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-emerald-100 p-3 rounded-2xl text-emerald-600"><Icon name="cube" /></div>
                <div>
                  <p className="font-bold text-slate-700">Bloques Sólidos</p>
                  <p className="text-sm text-slate-500">Ahora los cubos se fusionan visualmente para crear figuras continuas sin líneas internas.</p>
                </div>
              </div>
            </div>
            <button onClick={() => setShowHelp(false)} className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl shadow-lg transition-all uppercase tracking-widest text-sm">Entendido</button>
          </div>
        </div>
      )}

      <header className="no-print flex items-center justify-between px-6 py-4 bg-white border-b shadow-sm z-30">
        <div className="flex items-center space-x-8">
          <div className="flex flex-col">
            <h1 className="text-2xl font-black text-blue-600 tracking-tighter leading-none">IsoDraw</h1>
            <span className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mt-1">X:H | Y:V | Z:P</span>
          </div>
          <nav className="flex space-x-1 p-1 bg-slate-100 rounded-2xl">
            <button onClick={() => {setMode(Mode.CREATION); setView2D(false);}} className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all ${mode === Mode.CREATION ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 uppercase'}`}>
              <Icon name="brush" className="w-4 h-4" /><span>DIBUJAR</span>
            </button>
            <button onClick={() => setMode(Mode.INSPECTION)} className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all ${mode === Mode.INSPECTION ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 uppercase'}`}>
              <Icon name="eye" className="w-4 h-4" /><span>INSPECCIONAR</span>
            </button>
          </nav>
        </div>

        <div className="flex items-center space-x-3">
          {mode === Mode.CREATION ? (
            <div className="flex items-center space-x-2">
              <Toggle label="Visor" active={showMiniMap} onChange={setShowMiniMap} />
              <div className="w-px h-8 bg-slate-200 mx-2" />
              <ToolButton tool={Tool.POINTER} active={activeTool} onClick={(t) => { setActiveTool(t); setDrawingSegmentStart(null); }} icon="pointer" label="Seleccionar" />
              <ToolButton tool={Tool.ROTATOR} active={activeTool} onClick={(t) => { setActiveTool(t); setDrawingSegmentStart(null); }} icon="rotate" label="Rotar" />
              <ToolButton tool={Tool.ERASER} active={activeTool} onClick={(t) => { setActiveTool(t); setDrawingSegmentStart(null); }} icon="eraser" label="Borrar" />
              <ToolButton tool={Tool.BRUSH} active={activeTool} onClick={(t) => { setActiveTool(t); setDrawingSegmentStart(null); }} icon="brush" label="Colorear" />
              <div className="w-px h-8 bg-slate-200 mx-2" />
              <div className="flex bg-slate-100 rounded-2xl p-1.5 space-x-1">
                <button onClick={() => moveSelected(0, 1, 0)} className="p-2 hover:bg-white rounded-xl shadow-sm transition-all" title="Mover en Y (Arriba)"><Icon name="up" className="w-4 h-4" /></button>
                <button onClick={() => moveSelected(0, -1, 0)} className="p-2 hover:bg-white rounded-xl shadow-sm transition-all" title="Mover en Y (Abajo)"><Icon name="down" className="w-4 h-4" /></button>
                <button onClick={() => moveSelected(-1, 0, 0)} className="p-2 hover:bg-white rounded-xl shadow-sm transition-all" title="Mover en X (Izquierda)"><Icon name="left" className="w-4 h-4" /></button>
                <button onClick={() => moveSelected(1, 0, 0)} className="p-2 hover:bg-white rounded-xl shadow-sm transition-all" title="Mover en X (Derecha)"><Icon name="right" className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-6">
              <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                <button onClick={() => setView2D(false)} className={`px-6 py-2 rounded-xl text-xs font-black tracking-widest transition-all ${!view2D ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500'}`}>3D</button>
                <button onClick={() => setView2D(true)} className={`px-6 py-2 rounded-xl text-xs font-black tracking-widest transition-all ${view2D ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500'}`}>2D</button>
              </div>
              <Toggle label="Ejes" active={showAxes} onChange={setShowAxes} />
              <Toggle label="Rayos-X" active={transparent} onChange={setTransparent} />
              <div className="flex space-x-2">
                <button onClick={() => window.print()} className="p-3 text-slate-600 hover:bg-slate-100 rounded-2xl transition-all"><Icon name="print" /></button>
                <button onClick={() => {setRotation(INITIAL_ROTATION); setShowAxes(true); setTransparent(false); setObjects([]);}} className="p-3 text-slate-600 hover:bg-slate-100 rounded-2xl transition-all"><Icon name="reset" /></button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {mode === Mode.CREATION && (
          <aside className="no-print flex flex-col items-center w-28 py-8 bg-white border-r space-y-8 z-20 overflow-y-auto">
            <div className="flex flex-col space-y-4">
              <SidebarItem active={activeTool === Tool.CUBE} onClick={() => { setActiveTool(Tool.CUBE); setDrawingSegmentStart(null); }} icon="cube" label="CUBO" />
              <SidebarItem active={activeTool === Tool.FACE} onClick={() => { setActiveTool(Tool.FACE); setDrawingSegmentStart(null); }} icon="face" label="CARA" />
              <SidebarItem active={activeTool === Tool.SEGMENT} onClick={() => { setActiveTool(Tool.SEGMENT); setDrawingSegmentStart(null); }} icon="segment" label="LÍNEA" />
            </div>
            <div className="grid grid-cols-2 gap-3 p-3">
              {COLORS.map(color => (
                <button key={color} onClick={() => setActiveColor(color)} className={`w-9 h-9 rounded-2xl border-2 transition-all shadow-sm ${activeColor === color ? 'scale-110 ring-4 ring-blue-100 border-white' : 'border-white hover:scale-105'}`} style={{ backgroundColor: color }} />
              ))}
            </div>
          </aside>
        )}

        <main className="flex-1 relative bg-slate-50 overflow-hidden" onMouseDown={handleMouseDown}>
           {mode === Mode.CREATION && showMiniMap && !view2D && (
             <div className="absolute top-6 right-6 z-10 grid grid-cols-2 gap-3 p-3 bg-white/30 backdrop-blur-md rounded-[2.5rem] border border-white/50 shadow-2xl animate-in fade-in slide-in-from-right-4 duration-500 no-print">
               <OrthographicView objects={objects} title="PLANTA (XZ)" direction="top" size={70} />
               <OrthographicView objects={objects} title="ALZADO (XY)" direction="front" size={70} />
               <OrthographicView objects={objects} title="PERFIL D (ZY)" direction="right" size={70} />
               <OrthographicView objects={objects} title="PERFIL I" direction="left" size={70} />
             </div>
           )}

           <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
             <svg ref={svgRef} width="100%" height="100%" viewBox="-400 -400 800 800" className="overflow-visible pointer-events-auto">
                <g id="grid-base">
                   {!view2D && <IsometricGrid rotation={rotation} zLevel={0} />}
                   {!view2D && showAxes && (
                      <g strokeWidth="2" opacity="1">
                        <line x1={axisGuides.xn.x} y1={axisGuides.xn.y} x2={axisGuides.xp.x} y2={axisGuides.xp.y} stroke={AXIS_COLORS.X} strokeDasharray="5 3" opacity="0.4" />
                        <line x1={axisGuides.o.x} y1={axisGuides.o.y} x2={axisGuides.xp.x} y2={axisGuides.xp.y} stroke={AXIS_COLORS.X} />
                        <text x={axisGuides.xp.x + 8} y={axisGuides.xp.y} fill={AXIS_COLORS.X} className="text-[10px] font-black" dominantBaseline="middle">X+</text>
                        <text x={axisGuides.xn.x - 18} y={axisGuides.xn.y} fill={AXIS_COLORS.X} className="text-[10px] font-black opacity-50" dominantBaseline="middle">X-</text>

                        <line x1={axisGuides.yn.x} y1={axisGuides.yn.y} x2={axisGuides.yp.x} y2={axisGuides.yp.y} stroke={AXIS_COLORS.Y} strokeDasharray="5 3" opacity="0.4" />
                        <line x1={axisGuides.o.x} y1={axisGuides.o.y} x2={axisGuides.yp.x} y2={axisGuides.yp.y} stroke={AXIS_COLORS.Y} />
                        <text x={axisGuides.yp.x} y={axisGuides.yp.y - 8} fill={AXIS_COLORS.Y} className="text-[10px] font-black" textAnchor="middle">Y+</text>
                        <text x={axisGuides.yn.x} y={axisGuides.yn.y + 12} fill={AXIS_COLORS.Y} className="text-[10px] font-black opacity-50" textAnchor="middle">Y-</text>

                        <line x1={axisGuides.zn.x} y1={axisGuides.zn.y} x2={axisGuides.zp.x} y2={axisGuides.zp.y} stroke={AXIS_COLORS.Z} strokeDasharray="5 3" opacity="0.4" />
                        <line x1={axisGuides.o.x} y1={axisGuides.o.y} x2={axisGuides.zp.x} y2={axisGuides.zp.y} stroke={AXIS_COLORS.Z} />
                        <text x={axisGuides.zp.x + 8} y={axisGuides.zp.y} fill={AXIS_COLORS.Z} className="text-[10px] font-black" dominantBaseline="middle">Z+</text>
                        <text x={axisGuides.zn.x - 18} y={axisGuides.zn.y} fill={AXIS_COLORS.Z} className="text-[10px] font-black opacity-50" dominantBaseline="middle">Z-</text>
                      </g>
                   )}
                </g>

                <g id="objects-main">
                  {!view2D && sortedObjects.map(obj => {
                    const isSelected = selectedIds.includes(obj.id);
                    if (obj.type === 'cube') {
                      const { x, y, z } = obj.position;
                      const neighbors = {
                        top: cubeLookup.has(`${x},${y + 1},${z}`),
                        bottom: cubeLookup.has(`${x},${y - 1},${z}`),
                        right: cubeLookup.has(`${x + 1},${y},${z}`),
                        left: cubeLookup.has(`${x - 1},${y},${z}`),
                        front: cubeLookup.has(`${x},${y},${z + 1}`),
                        back: cubeLookup.has(`${x},${y},${z - 1}`),
                      };

                      return (
                        <CubeRenderer 
                          key={obj.id} position={obj.position} rotation={rotation} 
                          color={obj.color} transparent={transparent} 
                          onFaceClick={handleFaceClick} onFaceHover={handleFaceHover}
                          isGhost={isSelected} 
                          neighbors={neighbors}
                        />
                      );
                    } else if (obj.type === 'face') {
                      return (
                        <FaceRenderer key={obj.id} position={obj.position} rotation={rotation} orientation={obj.orientation!} color={obj.color} transparent={transparent} />
                      );
                    } else if (obj.type === 'segment' && obj.endPosition) {
                      return (
                        <SegmentRenderer key={obj.id} start={obj.position} end={obj.endPosition} rotation={rotation} color={obj.color} />
                      );
                    }
                    return null;
                  })}
                </g>

                {mode === Mode.CREATION && ghostPos && !view2D && (
                   <g opacity="0.4" style={{ pointerEvents: 'none' }}>
                      {activeTool === Tool.CUBE && <CubeRenderer position={ghostPos} rotation={rotation} color={activeColor} isGhost={true} />}
                      {activeTool === Tool.FACE && (
                        <FaceRenderer 
                          position={ghostPos} 
                          rotation={rotation} 
                          orientation={hoveredOrientation} 
                          color={activeColor} 
                          transparent={true} 
                        />
                      )}
                      {activeTool === Tool.SEGMENT && (
                        <>
                          <circle cx={project(ghostPos, rotation).x} cy={project(ghostPos, rotation).y} r="4" fill={activeColor} />
                          {drawingSegmentStart && <SegmentRenderer start={drawingSegmentStart} end={ghostPos} rotation={rotation} color={activeColor} />}
                        </>
                      )}
                   </g>
                )}
             </svg>
           </div>

           <div className="absolute bottom-10 left-10 flex space-x-4 no-print">
              <div className="p-6 bg-white shadow-xl rounded-3xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">CUBOS</span>
                <span className="text-3xl font-black text-blue-600">{cubesCount}</span>
              </div>
           </div>

           {mode === Mode.INSPECTION && view2D && (
             <div className="absolute inset-0 bg-slate-50/95 backdrop-blur-md flex flex-col items-center justify-center z-10 p-10 overflow-y-auto">
               <div className="grid grid-cols-2 gap-8 max-w-4xl">
                 <OrthographicView objects={objects} title="Planta (Superior XZ)" direction="top" size={200} />
                 <OrthographicView objects={objects} title="Alzado (Frontal XY)" direction="front" size={200} />
                 <OrthographicView objects={objects} title="Perfil Derecho (ZY)" direction="right" size={200} />
                 <OrthographicView objects={objects} title="Perfil Izquierdo" direction="left" size={200} />
               </div>
               <button onClick={() => setView2D(false)} className="mt-12 px-10 py-4 bg-slate-800 text-white font-black rounded-2xl shadow-xl uppercase tracking-widest text-sm transition-transform hover:scale-105 active:scale-95">Cerrar Vistas Detalladas</button>
             </div>
           )}
        </main>
      </div>
    </div>
  );
};

const ToolButton: React.FC<{ tool: Tool, active: Tool, onClick: (t: Tool) => void, icon: string, label: string }> = ({ tool, active, onClick, icon, label }) => (
  <button onClick={() => onClick(tool)} className={`flex items-center space-x-2 px-5 py-2.5 rounded-2xl transition-all ${active === tool ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}>
    <Icon name={icon} className="w-5 h-5" /><span className="text-[10px] font-black hidden xl:inline uppercase tracking-widest">{label}</span>
  </button>
);

const SidebarItem: React.FC<{ active: boolean, onClick: () => void, icon: string, label: string }> = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} className={`flex flex-col items-center justify-center w-20 h-20 rounded-3xl transition-all ${active ? 'bg-blue-600 text-white shadow-xl ring-8 ring-blue-50' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-700'}`}>
    <Icon name={icon} className="w-7 h-7" /><span className="text-[10px] font-black mt-3 uppercase tracking-tighter">{label}</span>
  </button>
);

const Toggle: React.FC<{ label: string, active: boolean, onChange: (v: boolean) => void }> = ({ label, active, onChange }) => (
  <div className="flex items-center space-x-4">
    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
    <button onClick={() => onChange(!active)} className={`w-12 h-7 rounded-full relative transition-all ${active ? 'bg-blue-500' : 'bg-slate-200'}`}>
      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all ${active ? 'left-6' : 'left-1'}`} />
    </button>
  </div>
);

export default App;
